/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHotkeys } from 'react-hotkeys-hook';

// @mui material components
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DialogActions from '@mui/material/DialogActions';
import { CircularProgress } from '@mui/material';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import MDTypography from 'components/MDTypography';
import MDSnackbar from 'components/MDSnackbar';
import * as Func from 'services/Function';

const initAttr = {
  name: '',
  country: '',
  tel: '',
};

function PosClient({ reload, setBlockScan, parent, sizeButton, buttonLoading, setButtonLoading }) {
  const mod = 'pointofsale';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [cboCountry, setCboCountry] = useState([]);
  const btnRef = useRef(null);
  const [notify, setNotify] = useState(null);

  useEffect(() => {
    setButtonLoading(loading);
  }, [loading]);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const closeModal = () => {
    setModalOpen(false);
    setBlockScan(false);

    setTimeout(function () {
      btnRef.current.blur();
    }, 100);
  };

  const view = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/initclient`, app.token, {
      lang: app.lang,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setCboCountry(result.country);

          if (parent.country === '') {
            setAttr({ ...initAttr, country: result.country[0].id });
          } else {
            setAttr({
              name: parent.client_name,
              tel: parent.client_tel,
              country: parent.client_country,
            });
          }
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const open = () => {
    setModalOpen(true);
    setBlockScan(true);
    view();
  };

  const save = () => {
    setSaveLoading(true);
    FetchData('OPTIONS', `/${mod}/saveclient`, app.token, {
      client_name: attr.name,
      client_tel: attr.tel,
      client_country: attr.country,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          reload();
          closeModal();
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          alert(result.msg);
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  const cancel = () => {
    setSaveLoading(true);
    FetchData('OPTIONS', `/${mod}/cancelclient`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          reload();
          closeModal();
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          alert(result.msg);
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  const renderList = () => {
    return (
      <MDBox sx={{ minHeight: 500 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {cboCountry.map((row, i) => (
                <Grid item xs={12} sm={3} md={2} lg={1} xl={2} key={row.id}>
                  {row.id === attr.country ? (
                    <MDButton variant='gradient' color='info' size='large' fullWidth>
                      {i + 1}. {row.code} {row.name}
                    </MDButton>
                  ) : (
                    <MDButton
                      variant='outlined'
                      color='dark'
                      size='large'
                      fullWidth
                      onClick={() => setAttr({ ...attr, country: row.id })}
                    >
                      {i + 1}. {row.code} {row.name}
                    </MDButton>
                  )}
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <MDBox className='card' p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDTypography variant='h5'>ข้อมูลลูกค้า</MDTypography>
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อลูกค้า'
              onChange={(e) => setAttr({ ...attr, name: e.target.value })}
              value={attr.name}
              variant='standard'
              fullWidth
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='เบอร์โทรศัพท์'
              onChange={(e) => setAttr({ ...attr, tel: e.target.value })}
              value={attr.tel}
              variant='standard'
              fullWidth
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-open'
        fullWidth
        onClose={closeModal}
      >
        <DialogTitle>
          <i className='fad fa-users color-danger' /> &nbsp; ข้อมูลลูกค้า
          <IconButton
            aria-label='close'
            onClick={closeModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={6} lg={9}>
              {renderList()}
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={3}>
              {renderInfo()}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={6}>
              <MDButton variant='text' color='error' onClick={cancel} disabled={saveLoading}>
                {saveLoading ? <CircularProgress size={18} color='inherit' /> : <Icon>cancel</Icon>}
                &nbsp;{t(`element.cancel`)}
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton variant='text' color='success' onClick={save} disabled={saveLoading}>
                {saveLoading ? <CircularProgress size={18} color='inherit' /> : <Icon>save</Icon>}
                &nbsp;{t(`element.save`)}
              </MDButton>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  useHotkeys('m', () => open());

  return (
    <MDBox>
      <Tooltip title='KEY : M'>
        {parent.country === '' ? (
          <MDButton
            variant='contained'
            color='dark'
            size='large'
            fullWidth
            sx={{ fontSize: sizeButton }}
            onClick={open}
            disabled={loading || buttonLoading}
            ref={btnRef}
          >
            {loading ? (
              <CircularProgress sx={{ fontSize: sizeButton }} color='inherit' />
            ) : (
              <i className='fad fa-users' />
            )}{' '}
            &nbsp; ลูกค้า
          </MDButton>
        ) : (
          <MDButton
            variant='contained'
            color='error'
            size='large'
            fullWidth
            sx={{ fontSize: sizeButton }}
            onClick={open}
            disabled={loading || buttonLoading}
            ref={btnRef}
          >
            {loading && <CircularProgress sx={{ fontSize: sizeButton }} color='inherit' />}
            {parent.country}
          </MDButton>
        )}
      </Tooltip>

      {renderOpen()}
      {notify}
    </MDBox>
  );
}

PosClient.propTypes = {
  reload: PropTypes.func.isRequired,
  setBlockScan: PropTypes.func.isRequired,
  parent: PropTypes.shape({
    client_name: PropTypes.string.isRequired,
    client_tel: PropTypes.string.isRequired,
    client_country: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
  }).isRequired,
  sizeButton: PropTypes.number.isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  setButtonLoading: PropTypes.func.isRequired,
};

export default PosClient;
